import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Tooltip,
} from '@mui/material';
import Add from '@mui/icons-material/Add';
import Delete from '@mui/icons-material/Delete';
import Edit from '@mui/icons-material/Edit';

import {
  useGetMyTeamsQuery,
  useCreateTeamMutation,
  useDeleteTeamMutation,
  useUpdateTeamMutation,
} from './teamsApi';

import { useConfirm } from '@/shared/components/ConfirmProvider';

const TeamsPage: React.FC = () => {
  const { data: teams = [] } = useGetMyTeamsQuery();
  const [createTeam] = useCreateTeamMutation();
  const [deleteTeam] = useDeleteTeamMutation();
  const confirm = useConfirm();
  const [updateTeam] = useUpdateTeamMutation();

  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);

  const handleSave = async () => {
    if (!name.trim()) return;
    if (editingId) {
      await updateTeam({ id: editingId, name, description }).unwrap();
    } else {
      await createTeam({ name, description }).unwrap();
    }
    setOpen(false);
    setEditingId(null);
    setName('');
    setDescription('');
  };

  const openCreate = () => {
    setEditingId(null);
    setName('');
    setDescription('');
    setOpen(true);
  };

  const openEdit = (team: { id: string; name: string; description?: string | null }) => {
    setEditingId(team.id);
    setName(team.name);
    setDescription(team.description || '');
    setOpen(true);
  };

  return (
    <Box sx={{ py: 4 }}>
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h4">Teams</Typography>
          <Button variant="contained" startIcon={<Add />} onClick={openCreate}>
            Create Team
          </Button>
        </Box>
        <List sx={{ mt: 2 }}>
          {teams.map((t) => (
            <ListItem
              key={t.id}
              secondaryAction={
                <Box>
                  <Tooltip title="Edit team">
                    <IconButton onClick={() => openEdit(t)}>
                      <Edit />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete team">
                    <IconButton
                      onClick={async () => {
                        const ok = await confirm({
                          title: 'Delete team?',
                          message: `Are you sure you want to delete "${t.name}"? Team members will lose any permissions granted through this team.`,
                        });
                        if (ok) deleteTeam(t.id);
                      }}
                    >
                      <Delete />
                    </IconButton>
                  </Tooltip>
                </Box>
              }
            >
              <ListItemText primary={t.name} secondary={t.description} />
            </ListItem>
          ))}
        </List>

        <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>{editingId ? 'Edit team' : 'Create team'}</DialogTitle>
          <DialogContent>
            <TextField
              label="Name"
              fullWidth
              sx={{ mt: 1 }}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <TextField
              label="Description"
              fullWidth
              sx={{ mt: 2 }}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleSave}>
              {editingId ? 'Save' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default TeamsPage;
